export const SALUTE_PAGES = [
  {
    path: '/cassa-malati',
    kind: 'area',
    area: 'Salute',
    areaPath: '/cassa-malati',
    eyebrow: 'Area Salute',
    title: 'Assicurazione salute in Svizzera: cassa malati, complementari e reddito.',
    summary: 'Confronta cassa malati LAMal, complementari, franchigia, infortuni e protezione del reddito con una consulenza che parte dalla tua situazione.',
    seoTitle: 'Cassa malati e assicurazione salute in Svizzera',
    seoDescription: 'Consulenza su cassa malati LAMal, complementari, franchigie, infortuni e protezione del reddito in Svizzera. Verifica coperture e alternative.',
    keywords: ['cassa malati svizzera', 'assicurazione salute svizzera', 'LAMal', 'assicurazioni complementari', 'broker assicurativo salute'],
    image: '/assets/soluzione-salute.png',
    updated: 'Giugno 2026',
    readingTime: '5 min',
    intro: [
      'In Svizzera l\u2019assicurazione malattia è obbligatoria per tutti, ma questo non significa che tutte le soluzioni siano uguali: premi, modelli assicurativi, franchigie e coperture complementari possono cambiare molto da una situazione all\u2019altra.',
      'Il nostro lavoro è aiutarti a leggere la tua situazione — età, famiglia, salute, lavoro e budget — prima di proporti qualsiasi cambiamento.',
      'Una verifica coordinata permette di distinguere ciò che è obbligatorio da ciò che è facoltativo e di capire dove premio, franchigia, modello assicurativo e prestazioni incidono davvero sulla tua scelta.',
    ],
    sections: [
      {
        id: 'due-livelli', type: 'text', title: 'Obbligatoria e facoltativa: due livelli distinti',
        paragraphs: [
          'L\u2019assicurazione di base (LAMal) è identica nelle prestazioni presso tutte le casse, ma cambia nel premio, nel modello assicurativo e nel servizio. Le assicurazioni complementari sono invece libere: ogni compagnia decide prestazioni, condizioni e accettazione.',
          'Per questo le analizziamo sempre separatamente: un buon premio sulla base non compensa una complementare sbagliata, e viceversa.',
        ],
      },
      {
        id: 'da-dove-iniziare', type: 'checklist', title: 'Da dove iniziare: la checklist',
        lead: 'Gli elementi che verifichiamo sempre prima di un confronto.',
        items: [
          'Franchigia annua e aliquota percentuale',
          'Modello assicurativo (standard, medico di famiglia, HMO, telemedicina)',
          'Rete di medici e fornitori convenzionati',
          'Coperture complementari attive e loro condizioni',
          'Situazione familiare e bisogni previsti nei prossimi anni',
          'Trattamenti medici già in corso o pianificati',
          'Scadenze e termini di disdetta delle polizze attuali',
        ],
      },
      {
        id: 'percorsi', type: 'paths', title: 'I percorsi dell\u2019area Salute',
        links: [
          { href: '/cassa-malati/calcolatore-premi', label: 'Calcolatore premi LAMal 2026–2027', text: 'Confronta i premi ufficiali per comune, franchigia, infortuni e modello assicurativo.' },
          { href: '/cassa-malati/cassa-malati-obbligatoria', label: 'Cassa malati obbligatoria', text: 'Cosa confrontare oltre al premio: modelli, franchigie, servizio.' },
          { href: '/cassa-malati/cassa-malati-facoltativa', label: 'Cassa malati facoltativa', text: 'Il ruolo delle complementari, i criteri di ammissione e gli errori da evitare.' },
          { href: '/guide/salute/cambio-cassa-malati-premi-disdetta', label: 'Quando fare il cambio', text: 'Premi, tempistiche e disdetta: la guida pratica al cambio di cassa.' },
          { href: '/guide/salute/assicurazione-prenatale', label: 'Assicurazione prenatale', text: 'Cosa organizzare prima della nascita di un figlio.' },
        ],
      },
      {
        id: 'errori', type: 'errors', title: 'Errori frequenti',
        items: [
          'Scegliere solo in base al premio mensile più basso',
          'Non verificare il modello assicurativo e i suoi vincoli',
          'Confondere assicurazione di base e complementare',
          'Disdire una complementare prima di avere la conferma scritta della nuova',
          'Non considerare i bisogni futuri della famiglia',
        ],
      },
    ],
    faq: [
      { q: 'La copertura di base è uguale presso tutte le casse?', a: 'Le prestazioni dell\u2019assicurazione di base sono definite dalla legge e sono le stesse ovunque. Cambiano premio, modello assicurativo, gestione amministrativa e servizio clienti.' },
      { q: 'Posso avere base e complementare presso compagnie diverse?', a: 'Sì, è possibile e in alcuni casi sensato. Va però valutato caso per caso, perché alcune compagnie legano sconti o condizioni alla combinazione delle due coperture.' },
      { q: 'Quando posso cambiare cassa malati?', a: 'Per la copertura di base il cambio è possibile a fine anno, rispettando i termini di disdetta. Le complementari seguono invece le condizioni del contratto: spesso preavvisi di 3 mesi. I dettagli nella nostra guida al cambio.' },
      { q: 'Le complementari possono rifiutarmi?', a: 'Sì. A differenza della base, le assicurazioni complementari possono richiedere un questionario sanitario e applicare riserve, esclusioni o rifiuti. Per questo non si disdice mai prima di avere una conferma scritta.' },
    ],
    cta: { title: 'Richiedi una verifica della tua situazione sanitaria', text: 'Raccontaci la tua situazione attuale: controlliamo modello, franchigia e complementari e ti mostriamo le alternative.', label: 'Richiedi una verifica' },
    related: ['/guide/salute/cambio-cassa-malati-premi-disdetta', '/guide/salute/assicurazione-prenatale'],
  },
  {
    path: '/cassa-malati/cassa-malati-obbligatoria',
    kind: 'service',
    area: 'Salute',
    areaPath: '/cassa-malati',
    eyebrow: 'Salute',
    title: 'Cassa malati obbligatoria: cosa confrontare oltre al premio.',
    summary: 'Le prestazioni di base sono uguali per legge. A fare la differenza sono modello assicurativo, franchigia e servizio.',
    updated: 'Giugno 2026',
    readingTime: '7 min',
    intro: [
      'Molti confronti tra casse malati si fermano al premio mensile. È un criterio importante, ma non sufficiente: due offerte con premi simili possono funzionare in modo molto diverso quando hai effettivamente bisogno di cure.',
    ],
    sections: [
      {
        id: 'cosa-valutare', type: 'checklist', title: 'Che cosa valutare',
        items: [
          'Premio mensile e sua evoluzione negli anni',
          'Franchigia annua e aliquota percentuale',
          'Modello assicurativo e vincoli di accesso alle cure',
          'Copertura infortuni: inclusa o esclusa',
          'Gestione amministrativa: app, rimborso spese, tempi',
          'Disponibilità e qualità del servizio clienti',
        ],
      },
      {
        id: 'modelli', type: 'cards', title: 'I modelli assicurativi',
        lead: 'Ogni modello cambia il modo in cui accedi alle cure. Non esiste il modello migliore: esiste quello coerente con le tue abitudini.',
        cards: [
          { title: 'Modello standard', text: 'Libera scelta del medico. Premio più alto, massima flessibilità. Adatto a chi vuole decidere senza vincoli.' },
          { title: 'Medico di famiglia', text: 'Il primo contatto è sempre il tuo medico di riferimento, che coordina visite e specialisti. Premio ridotto.' },
          { title: 'HMO', text: 'Ti curi all\u2019interno di una rete di medici convenzionata. Premi interessanti, ma meno libertà di scelta.' },
          { title: 'Telemedicina', text: 'Il primo contatto avviene per telefono o video con un centro medico. Comodo, ma richiede disciplina nel seguire il percorso.' },
        ],
      },
      {
        id: 'franchigie', type: 'cards', title: 'Franchigie e quota-parte: quanto paghi davvero',
        lead: 'La franchigia è l’importo annuo che paghi prima che la cassa inizi a partecipare ai costi. Dopo averla raggiunta si applica anche la quota-parte.',
        cards: [
          { title: 'Franchigie per adulti', text: 'La franchigia ordinaria è CHF 300. Le opzioni disponibili sono CHF 500, 1’000, 1’500, 2’000 e 2’500 per anno civile.' },
          { title: 'Franchigie per bambini', text: 'Fino a 18 anni la franchigia ordinaria è CHF 0. Si possono scegliere CHF 100, 200, 300, 400, 500 o 600; la cassa non è tenuta a offrire ogni opzione.' },
          { title: 'La quota-parte', text: 'Una volta superata la franchigia, paghi il 10% delle ulteriori prestazioni LAMal: al massimo CHF 700 all’anno per un adulto e CHF 350 per un bambino.' },
        ],
      },
      {
        id: 'scelta-franchigia', type: 'warning', title: 'CHF 300 o CHF 2’500: il confronto che conta',
        text: 'Come regola pratica, se prevedi prestazioni LAMal superiori a circa CHF 1’400 nell’anno, partiamo dalla franchigia CHF 300. Le franchigie intermedie raramente hanno senso economico: spesso non offrono né il risparmio della CHF 2’500 né la protezione della CHF 300. La soglia va comunque verificata confrontando il risparmio di premio effettivo della tua cassa e la tua capacità di sostenere una spesa imprevista.',
      },
      {
        id: 'attenzione-cambio', type: 'warning', title: 'Attenzione ai tempi del cambio',
        text: 'Il cambio dell\u2019assicurazione di base è possibile a fine anno e richiede il rispetto dei termini di disdetta. Prima di firmare una nuova polizza verifica le scadenze: trovi il calendario completo nella nostra guida al cambio di cassa malati.',
      },
    ],
    faq: [
      { q: 'Posso cambiare cassa malati ogni anno?', a: 'Per l\u2019assicurazione di base sì, rispettando i termini di disdetta (di regola entro fine novembre, con decorrenza a fine anno). Le complementari seguono invece i termini del proprio contratto.' },
      { q: 'Quando conviene togliere la copertura infortuni?', a: 'Se lavori almeno 8 ore settimanali presso lo stesso datore di lavoro, sei già assicurato contro gli infortuni tramite la LAINF e puoi escludere la copertura dalla cassa malati, riducendo il premio.' },
      { q: 'Cosa cambia tra i diversi modelli assicurativi?', a: 'Cambia il primo punto di contatto quando hai bisogno di cure: libera scelta, medico di famiglia, rete HMO o telemedicina. A parità di prestazioni di legge, i modelli alternativi offrono premi più bassi in cambio di vincoli nel percorso di cura.' },
      { q: 'Posso avere base e complementare presso compagnie diverse?', a: 'Sì, è possibile. Va valutato caso per caso: a volte conviene separare, a volte le condizioni combinate sono più vantaggiose.' },
    ],
    cta: { title: 'Confronta la tua soluzione attuale', text: 'Mandaci i dati della tua polizza attuale: simuliamo franchigia e modello sui tuoi consumi reali.', label: 'Confronta la tua soluzione' },
    related: ['/guide/salute/cambio-cassa-malati-premi-disdetta', '/cassa-malati/cassa-malati-facoltativa'],
  },
  {
    path: '/cassa-malati/cassa-malati-facoltativa',
    kind: 'service',
    area: 'Salute',
    areaPath: '/cassa-malati',
    eyebrow: 'Salute',
    title: 'Cassa malati facoltativa: il valore (e i limiti) delle complementari.',
    summary: 'Le assicurazioni complementari coprono ciò che la base non copre. Ma non sono tutte uguali, e l\u2019accettazione non è automatica.',
    updated: 'Giugno 2026',
    readingTime: '7 min',
    intro: [
      'Ospedale, cure dentarie, medicina complementare, occhiali: le complementari possono fare una grande differenza concreta. Possono però anche rivelarsi costose e poco utili se scelte senza leggere le condizioni.',
    ],
    sections: [
      {
        id: 'a-cosa-servono', type: 'cards', title: 'A cosa possono servire',
        cards: [
          { title: 'Reparto ospedaliero', text: 'Camera privata o semiprivata e libera scelta del medico in ospedale, in Svizzera o all\u2019estero.' },
          { title: 'Cure dentarie', text: 'Contributi per trattamenti e ortodonzia, soprattutto per i bambini. Verifica sempre limiti annui e periodi d\u2019attesa.' },
          { title: 'Medicina complementare', text: 'Rimborsi per trattamenti non convenzionali eseguiti da terapisti riconosciuti.' },
          { title: 'Occhiali e lenti', text: 'Contributi periodici per ausili visivi, con importi e frequenze variabili tra compagnie.' },
          { title: 'Sport e prevenzione', text: 'Contributi per abbonamenti fitness, corsi e check-up di prevenzione.' },
          { title: 'Estero e salvataggio', text: 'Copertura delle cure d\u2019emergenza all\u2019estero e dei costi di trasporto e salvataggio.' },
        ],
      },
      {
        id: 'non-tutte-uguali', type: 'text', title: 'Non tutte le complementari sono uguali',
        paragraphs: [
          'Nomi simili possono nascondere prestazioni molto diverse: importi massimi annui, franchigie, periodi d\u2019attesa, elenchi di terapisti riconosciuti. Il confronto va fatto sulle condizioni generali, non sul nome del prodotto.',
        ],
      },
      {
        id: 'questionario', type: 'warning', title: 'Il questionario sanitario',
        text: 'Per le complementari la compagnia può richiedere un questionario sul tuo stato di salute e applicare riserve, esclusioni o rifiuti. Rispondi sempre in modo completo e corretto: omissioni possono portare al rifiuto delle prestazioni proprio quando ne hai bisogno.',
      },
      {
        id: 'prima-di-cambiare', type: 'checklist', title: 'Prima di cambiare',
        items: [
          'Confrontare le condizioni generali, non solo il premio',
          'Verificare i limiti annui di rimborso per ogni prestazione',
          'Controllare i periodi d\u2019attesa (ad esempio per la maternità)',
          'Attendere la conferma scritta della nuova compagnia',
          'Solo dopo: disdire la copertura precedente e conservare la prova di invio',
        ],
      },
    ],
    faq: [
      { q: 'Posso essere rifiutato da una complementare?', a: 'Sì. Le assicurazioni complementari sono libere di accettare, applicare riserve o rifiutare. Per questo la tempistica del cambio è delicata e va pianificata.' },
      { q: 'Posso cambiare solo la base e tenere la complementare?', a: 'Sì, in genere è possibile. Attenzione però a eventuali sconti legati alla combinazione e alle condizioni del contratto complementare.' },
      { q: 'Cosa succede se dimentico una patologia nel questionario?', a: 'La compagnia può contestare il contratto e rifiutare le prestazioni collegate. Meglio dichiarare tutto e accettare un\u2019eventuale riserva piuttosto che rischiare un rifiuto al momento del bisogno.' },
      { q: 'Posso assicurare un bambino prima della nascita?', a: 'Sì, con l\u2019assicurazione prenatale molte compagnie accettano il bambino senza questionario sanitario. È uno dei pochi casi in cui l\u2019accesso alle complementari è semplificato: ne parliamo nella guida dedicata.' },
    ],
    cta: { title: 'Verifica le tue coperture complementari', text: 'Analizziamo le condizioni delle tue complementari e le confrontiamo con le alternative sul mercato.', label: 'Verifica le complementari' },
    related: ['/guide/salute/assicurazione-prenatale', '/guide/salute/cambio-cassa-malati-premi-disdetta'],
  },
  {
    path: '/guide/salute/cambio-cassa-malati-premi-disdetta',
    kind: 'guide',
    area: 'Salute',
    areaPath: '/cassa-malati',
    eyebrow: 'Guida pratica · Salute',
    title: 'Cambiare cassa malati: premi, tempistiche e disdetta.',
    summary: 'Il calendario del cambio, i documenti da verificare e gli errori da evitare quando arrivano i nuovi premi.',
    updated: 'Giugno 2026',
    readingTime: '8 min',
    intro: [
      'Ogni autunno le casse comunicano i premi dell\u2019anno successivo. È il momento giusto per confrontare, ma anche quello in cui si commettono più errori: disdette inviate tardi, modelli scelti in fretta, complementari date per scontate.',
      'Questa guida ti accompagna in ordine: prima il confronto, poi la decisione, infine la disdetta corretta.',
    ],
    sections: [
      {
        id: 'prima-di-cambiare', type: 'text', title: 'Prima di cambiare',
        paragraphs: ['Prima ancora di guardare i premi, metti in ordine la tua situazione attuale.'],
        bullets: [
          'Confronta premio e modello assicurativo, non solo il premio',
          'Verifica franchigia e aliquota della polizza attuale',
          'Distingui tra assicurazione di base e complementari: hanno regole diverse',
          'Controlla eventuali situazioni amministrative pendenti o premi arretrati',
        ],
      },
      {
        id: 'calendario', type: 'timeline', title: 'Il calendario del cambio',
        lead: 'Una sequenza indicativa: i termini effettivi vanno sempre verificati ogni anno, perché possono variare.',
        steps: [
          { label: 'Ottobre', title: 'Arrivano i nuovi premi', text: 'Le casse comunicano i premi dell\u2019anno successivo. È il momento di raccogliere i dati e confrontare.' },
          { label: 'Novembre', title: 'Il confronto', text: 'Simula franchigia e modello sui tuoi consumi reali. Se cambi, richiedi la conferma della nuova cassa.' },
          { label: 'Fine novembre', title: 'Il termine di disdetta', text: 'La disdetta della copertura di base deve giungere alla vecchia cassa entro il termine previsto, in forma scritta e con prova di invio.' },
          { label: '1° gennaio', title: 'La nuova copertura', text: 'La nuova assicurazione di base decorre dal primo gennaio. Conserva la conferma e i documenti.' },
        ],
      },
      {
        id: 'disdetta', type: 'text', title: 'Come inviare la disdetta',
        paragraphs: ['La disdetta deve essere chiara, completa e tracciabile.'],
        bullets: [
          'Forma scritta, preferibilmente lettera raccomandata',
          'Nome, data di nascita e numero di polizza o di assicurato',
          'Data di decorrenza richiesta (di regola 31 dicembre)',
          'Firma e data',
          'Conserva sempre la prova di invio e di ricezione',
        ],
      },
      {
        id: 'base-complementare', type: 'warning', title: 'Base e complementare: regole diverse',
        text: 'Le regole di cambio dell\u2019assicurazione di base e delle complementari possono essere molto diverse: preavvisi, durata minima, questionario sanitario. Non disdire mai una copertura complementare prima di avere la conferma scritta di accettazione della nuova soluzione.',
      },
      {
        id: 'checklist', type: 'checklist', title: 'La checklist del cambio',
        items: [
          'Ho confrontato il modello assicurativo, non solo il premio',
          'Ho controllato la franchigia più adatta ai miei consumi',
          'Ho verificato la copertura infortuni (inclusa o esclusa)',
          'Ho ricevuto conferma scritta della nuova soluzione',
          'Ho inviato la disdetta entro il termine',
          'Ho conservato la prova di invio',
        ],
      },
    ],
    faq: [
      { q: 'Entro quando devo disdire la cassa malati?', a: 'Per l\u2019assicurazione di base la disdetta deve di regola giungere alla cassa entro fine novembre, con effetto al 31 dicembre. Verifica sempre il termine esatto comunicato con i nuovi premi.' },
      { q: 'La disdetta via email è valida?', a: 'Dipende dalla cassa: alcune accettano forme digitali, altre richiedono la lettera. Il metodo più sicuro resta la raccomandata con prova di ricezione.' },
      { q: 'Posso cambiare anche le complementari nello stesso momento?', a: 'Le complementari hanno termini propri, spesso diversi dalla base. Prima di disdire serve la conferma scritta di accettazione della nuova compagnia.' },
      { q: 'Cosa succede se ho premi arretrati?', a: 'Situazioni amministrative pendenti possono complicare il cambio. Meglio chiarirle prima, eventualmente con l\u2019aiuto di un consulente.' },
    ],
    cta: { title: 'Fai controllare la tua disdetta', text: 'Prima di inviare la disdetta, facciamola verificare insieme: termini, forma e contenuti corretti.', label: 'Fai controllare la disdetta' },
    related: ['/cassa-malati/cassa-malati-obbligatoria', '/cassa-malati/cassa-malati-facoltativa'],
  },
  {
    path: '/guide/salute/assicurazione-prenatale',
    kind: 'guide',
    area: 'Salute',
    areaPath: '/cassa-malati',
    eyebrow: 'Guida pratica · Salute',
    title: 'Assicurazione prenatale: cosa organizzare prima della nascita.',
    summary: 'Alcune coperture per il tuo bambino sono più semplici da attivare prima del parto. Ecco cosa valutare e quando.',
    updated: 'Giugno 2026',
    readingTime: '6 min',
    intro: [
      'Con l\u2019arrivo di un figlio gli adempimenti si accumulano. Alcune decisioni assicurative, però, conviene prenderle durante la gravidanza: l\u2019iscrizione prenatale permette spesso di accettare il bambino senza questionario sanitario.',
    ],
    sections: [
      {
        id: 'perche-prima', type: 'text', title: 'Perché pensarci prima',
        paragraphs: [
          'Molte compagnie permettono di iscrivere il bambino prima della nascita accettandolo senza riserve, anche per le complementari. Dopo il parto, invece, può essere richiesto un questionario sanitario.',
          'Inoltre, nei primi mesi dopo la nascita avrai già molte cose da gestire: anticipare le decisioni assicurative toglie un pensiero.',
        ],
      },
      {
        id: 'cosa-valutare', type: 'checklist', title: 'Cosa valutare',
        items: [
          'Iscrizione all\u2019assicurazione di base del bambino',
          'Coperture complementari: ospedale, cure dentarie, medicina complementare',
          'Reparto ospedaliero desiderato per la nascita e per il bambino',
          'Protezione finanziaria della famiglia (responsabilità civile, mobilia)',
          'Previdenza e protezione del reddito dei genitori',
        ],
      },
      {
        id: 'timeline', type: 'timeline', title: 'La timeline consigliata',
        steps: [
          { label: 'Durante la gravidanza', text: 'Informarsi sulle casse, confrontare le complementari e valutare l\u2019iscrizione prenatale.' },
          { label: 'Poco prima del parto', text: 'Confermare le scelte, preparare i documenti e i dati dei genitori.' },
          { label: 'Subito dopo la nascita', text: 'Comunicare la nascita alla cassa e completare l\u2019iscrizione alla base entro i termini previsti.' },
          { label: 'Primi mesi', text: 'Verificare l\u2019insieme delle coperture familiari: responsabilità civile, mobilia, previdenza dei genitori.' },
        ],
      },
      {
        id: 'documenti', type: 'text', title: 'Documenti e informazioni utili',
        bullets: [
          'Dati anagrafici dei genitori e numero AVS',
          'Data presunta del parto',
          'Domicilio e cassa malati scelta',
          'Elenco delle coperture complementari desiderate',
          'Eventuali esigenze particolari note in gravidanza',
        ],
      },
    ],
    faq: [
      { q: 'Quando devo iniziare a informarmi?', a: 'L\u2019ideale è il secondo trimestre di gravidanza: hai tempo di confrontare con calma e completare l\u2019iscrizione prenatale.' },
      { q: 'La copertura parte dalla nascita?', a: 'Con l\u2019iscrizione prenatale la copertura può decorrere direttamente dalla nascita, senza interruzioni. Le condizioni esatte dipendono dalla compagnia.' },
      { q: 'Quali coperture richiedono una valutazione preventiva?', a: 'Dopo la nascita le complementari possono richiedere un questionario sanitario sul bambino. Prima della nascita, invece, molte compagnie accettano senza riserve.' },
      { q: 'Posso modificare la scelta dopo la nascita?', a: 'La base può essere cambiata secondo i termini ordinari. Le complementari vanno valutate con più cautela, perché un nuovo accesso può richiedere il questionario.' },
    ],
    cta: { title: 'Prepara la copertura del tuo bambino', text: 'Ti aiutiamo a confrontare le opzioni prenatali e a organizzare tutto prima del parto.', label: 'Prepara la copertura' },
    related: ['/cassa-malati/cassa-malati-facoltativa', '/guide/privati/trasferirsi-in-svizzera'],
  },
];
